import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import { ProfileContainer, Box, Typography, ImageUploader } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import UserProfileService from "services/medkit/userProfile.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { IUserInfo } from "commons/interfaces";
import { storeUserInfo } from "store/slice/user.slice";


const userProfileService = new UserProfileService();

const ProfileScreen = () => {

    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user);

    const [reportCard1, setReportCard1] = useState<number>(0);
    const [reportCard2, setReportCard2] = useState<number>(0);
    const [signaturePhoto, setSignaturePhoto] = useState<string>("");
    const [userProfileMirs, setUserProfileMirs] = useState<{
        id?: string,
        userId?: string,
        organizationId?: string,
    }>();

    useEffect(() => {

        (async () => {
            if (!Helpers.isNullOrEmpty(user?.userInfo?.userProfile?.identityId)) {
                try {
                    const result = await userProfileService.detail(user?.userInfo?.userProfile?.identityId);

                    const extraInfo = Helpers.converStringToJson(result?.extraInformation);

                    setReportCard1(result?.report?.[0]?.sum || 0);
                    setReportCard2(result?.report?.[1]?.sum || 0);
                    setUserProfileMirs({
                        id: result?.id,
                        userId: result?.userId,
                        organizationId: result?.organizationId,
                    });

                    if (!Helpers.isNullOrEmpty(extraInfo?.signatureUrl?.photoId)) {
                        setSignaturePhoto(extraInfo?.signatureUrl?.photoId);
                    }
                } catch (error) {
                    setUserProfileMirs({
                        userId: user?.userInfo?.userProfile?.identityId,
                        organizationId: user?.workingOrganization,
                    });
                }
            }
        })();

    }, [user?.userInfo?.userProfile?.identityId]);

    const renderSignaturePhoto = () => {
        return (
            <Box id={"signature_photo"} padding={2}>
                <Typography variant="h5">{Strings.PROFILE.SIGNATURE_PHOTO}</Typography>
                <Box padding={2}>
                    <ImageUploader
                        width={150}
                        height={150}
                        imageRatio={"1:1"}
                        optimize={false}
                        fileId={signaturePhoto}
                        onChangeImage={async (data) => {
                            try {
                                setSignaturePhoto(data?.id);

                                const signatureUrl = {
                                    photoId: data?.id,
                                    photoUrl: data?.accessUrl
                                };

                                await userProfileService.update({
                                    id: userProfileMirs?.id,
                                    userId: userProfileMirs?.userId,
                                    organizationId: userProfileMirs?.organizationId,
                                    extraInformation: JSON.stringify({ signatureUrl: signatureUrl }),
                                });
                            } catch (error) { } finally { }
                        }}
                    />
                </Box>
            </Box>
        );
    };

    const renderSummaryReports = () => {
        return (
            <Box id={"summary_report"} padding={2}>
                <Typography variant="h5">{Strings.CHART.STATISTICS}</Typography>
                <Box gap={3} paddingTop={3} paddingBottom={2} display="flex" flexWrap="wrap" alignItems="center">
                    <ComplexStatisticsCard
                        key={"1"}
                        color={"warning"}
                        count={reportCard1}
                        unit={Strings.REPORT.REPORT}
                        title={Strings.REPORT.OBLIGATION}
                        iconName={"assignment_turned_in"}
                    />
                    <ComplexStatisticsCard
                        key={"2"}
                        color={"info"}
                        count={reportCard2}
                        unit={Strings.REPORT.REPORT}
                        title={Strings.REPORT.VOLUNTEER}
                        iconName={"assignment_turned_in"}
                    />
                </Box>
            </Box>
        );
    };

    const ComplexStatisticsCard = (data: {
        title: string;
        count: number;
        unit?: string;
        iconName: string;
        color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    }) => (
        <Card sx={{ width: "258.750px", border: "0.5px solid #cdcdcd" }}>
            <Box padding={2} pb={1} display="flex" justifyContent="space-between">
                <Box
                    mt={-3}
                    width="4rem"
                    height="4rem"
                    display="flex"
                    borderRadius="xl"
                    variant="gradient"
                    alignItems="center"
                    bgColor={data.color}
                    justifyContent="center"
                    coloredShadow={data.color}
                    color={data.color === "light" ? "dark" : "white"}
                >
                    <Icon fontSize="medium" color="inherit">
                        {data.iconName}
                    </Icon>
                </Box>
                <Box p={1} textAlign="right" lineHeight={1.25}>
                    <Typography variant="h6">
                        {data.title}
                    </Typography>
                </Box>
            </Box>
            <Divider />
            <Box padding={2} pt={1} display={"flex"} alignItems={"flex-end"} justifyContent={"space-between"}>
                <Box width="4rem" display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="h2">
                        {data.count}
                    </Typography>
                </Box>
                {data.unit &&
                    <Box>
                        <Typography color="secondary" variant="button" fontWeight="bold">
                            {data.unit}
                        </Typography>
                    </Box>
                }
            </Box>
        </Card>
    );

    return (
        <DashboardLayout
            isPermission
            title={Strings.PROFILE.TITLE}
            route={[{ title: Strings.PROFILE.TITLE, route: "" }]}
        >
            <Box mt={1}>
                <ProfileContainer
                    isMultiCardContainer={false}

                    hiddenCardInfoProfile={{
                        hiddenIdCardInfo: true,
                        hiddenPassportInfo: true,
                        // hiddenUserProfileByOrganization: true,
                    }}
                    hiddenCardChangeEmail
                    hiddenCardChangeUserContact
                    hiddenTabTwoFactorAuthentication

                    onDataUserProfile={(data) => {
                        const newUserInfo: IUserInfo = {
                            ...user?.userInfo,
                            userProfile: {
                                ...user?.userInfo?.userProfile,
                                avatarId: data?.avatarId,
                                avatarUrl: data?.avatarUrl,
                                email: data?.email,
                                gender: data?.gender,
                                fullName: data?.fullName,
                                phoneNumber: data?.phoneNumber,
                                birthDate: data?.birthDate as string,
                            },
                        };

                        dispatch(storeUserInfo(newUserInfo));

                    }}

                    sideNavItems={[
                        { href: "signature_photo", icon: "perm_media_outlined", label: Strings.PROFILE.SIGNATURE_PHOTO },
                        { href: "summary_report", icon: "data_exploration_outlined", label: Strings.CHART.STATISTICS },
                    ]}
                    listChildren={[
                        renderSignaturePhoto(),
                        renderSummaryReports(),
                    ]}
                />
            </Box>
        </DashboardLayout>
    );
};

export default ProfileScreen;
